import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface Props {
    text: string;
    children: React.ReactNode;
    position?: 'top' | 'bottom';
    delay?: number;
    maxWidth?: number;
}

const BG = '#1e293b';
const FG = '#f1f5f9';
const ARROW = 6;
const GAP = 6;

const Tooltip: React.FC<Props> = ({
    text,
    children,
    position = 'bottom',
    delay = 400,
    maxWidth = 360,
}) => {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState<{ top: number; left: number; arrowLeft: number } | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const show = useCallback(() => {
        timerRef.current = setTimeout(() => setVisible(true), delay);
    }, [delay]);

    const hide = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setVisible(false);
        setCoords(null);
    }, []);

    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    useEffect(() => {
        if (!visible || !wrapperRef.current) return;
        const rect = wrapperRef.current.getBoundingClientRect();
        const tipW = Math.min(maxWidth, window.innerWidth - 24);
        const centerX = rect.left + rect.width / 2;

        let left = centerX - tipW / 2;
        let arrowLeft = tipW / 2;

        if (left < 12) {
            arrowLeft += left - 12;
            left = 12;
        } else if (left + tipW > window.innerWidth - 12) {
            const shift = (left + tipW) - (window.innerWidth - 12);
            arrowLeft += shift;
            left -= shift;
        }

        arrowLeft = Math.max(ARROW + 4, Math.min(arrowLeft, tipW - ARROW - 4));

        const top = position === 'bottom'
            ? rect.bottom + GAP + window.scrollY
            : rect.top - GAP + window.scrollY;

        setCoords({ top, left, arrowLeft });
    }, [visible, maxWidth, position]);

    const below = position === 'bottom';

    return (
        <div
            ref={wrapperRef}
            className="relative inline-flex"
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
        >
            {children}
            {visible && coords && createPortal(
                <div
                    style={{
                        position: 'absolute',
                        zIndex: 99999,
                        pointerEvents: 'none',
                        left: coords.left,
                        width: Math.min(maxWidth, window.innerWidth - 24),
                        ...(below
                            ? { top: coords.top }
                            : { top: coords.top, transform: 'translateY(-100%)' }),
                    }}
                >
                    {/* Arrow */}
                    <div
                        style={{
                            position: 'absolute',
                            left: coords.arrowLeft,
                            marginLeft: -ARROW,
                            width: 0,
                            height: 0,
                            borderLeft: `${ARROW}px solid transparent`,
                            borderRight: `${ARROW}px solid transparent`,
                            ...(below
                                ? { top: 0, marginTop: -ARROW, borderBottom: `${ARROW}px solid ${BG}` }
                                : { bottom: 0, marginBottom: -ARROW, borderTop: `${ARROW}px solid ${BG}` }),
                        }}
                    />
                    {/* Bubble */}
                    <div
                        style={{
                            background: BG,
                            color: FG,
                            padding: '8px 14px',
                            borderRadius: 8,
                            fontSize: 12,
                            lineHeight: 1.6,
                            fontWeight: 500,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        }}
                    >
                        {text}
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
};

export default Tooltip;
